import { useState } from "react";
import { Search, ExternalLink, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const RESTRICTED_WORDS = ["bank", "trust", "insurance", "university", "school", "college"];
const REQUIRED_SUFFIXES = ["llc", "l.l.c.", "l.l.c", "limited liability company"];

export default function LlcNameChecker() {
  const [name, setName] = useState("");
  const [result, setResult] = useState(null); // null | { valid, warnings, errors }

  const checkName = () => {
    if (!name.trim()) return;

    const lower = name.trim().toLowerCase();
    const errors = [];
    const warnings = [];

    // Check for LLC suffix
    const hasSuffix = REQUIRED_SUFFIXES.some((s) => lower.endsWith(s));
    if (!hasSuffix) {
      errors.push("Name must end with 'LLC', 'L.L.C.', or 'Limited Liability Company'");
    }

    // Check for restricted words
    RESTRICTED_WORDS.forEach((word) => {
      if (lower.includes(word)) {
        warnings.push(`Contains restricted word "${word}" — may require special approval`);
      }
    });

    // Check minimum length (name part without suffix)
    const nameWithoutSuffix = lower.replace(/\s*(llc|l\.l\.c\.?|limited liability company)\s*$/i, "").trim();
    if (nameWithoutSuffix.length < 3) {
      errors.push("LLC name should be at least 3 characters (excluding the suffix)");
    }

    // Check for special characters
    if (/[^a-zA-Z0-9\s&\-'.]/.test(name.trim())) {
      warnings.push("Contains special characters that may not be accepted");
    }

    setResult({
      valid: errors.length === 0,
      errors,
      warnings,
      name: name.trim(),
    });
  };

  const sosSearchUrl = `https://www.sos.state.tx.us/corp/sosda/index.shtml`;
  const comptrollerUrl = `https://comptroller.texas.gov/taxes/franchise/account-status/search`;

  return (
    <div data-testid="llc-name-checker" className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
      <div className="mb-4 flex items-center gap-2">
        <Search className="h-5 w-5 text-[#1E3A8A]" />
        <h4 className="font-heading text-base font-bold text-[var(--c-text)]">LLC Name Checker</h4>
      </div>
      <p className="mb-4 text-sm text-[var(--c-muted)]">
        Enter your desired LLC name to check basic requirements, then search official Texas databases.
      </p>

      {/* Input */}
      <div className="flex gap-2">
        <input
          data-testid="llc-name-input"
          type="text"
          value={name}
          onChange={(e) => { setName(e.target.value); setResult(null); }}
          onKeyDown={(e) => e.key === "Enter" && checkName()}
          placeholder="e.g., Sunrise Ventures LLC"
          className="flex-1 rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2.5 text-sm text-[var(--c-text)] placeholder:text-[var(--c-muted)] focus:border-[#1E3A8A] focus:outline-none focus:ring-1 focus:ring-[#1E3A8A]/20"
        />
        <button
          data-testid="check-name-button"
          onClick={checkName}
          disabled={!name.trim()}
          className="flex items-center gap-1.5 rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A] px-4 py-2.5 text-sm font-bold text-white shadow-[2px_2px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
        >
          <Search className="h-4 w-4" />
          Check
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="mt-4 space-y-3">
          {/* Status */}
          <div className={`flex items-center gap-2 rounded-lg border-2 p-3 ${
            result.valid
              ? "border-[#A3E6D5] bg-[#A3E6D5]/10"
              : "border-[#FF4D4D]/50 bg-[#FF4D4D]/5"
          }`}>
            {result.valid ? (
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#1A1918]" />
            ) : (
              <XCircle className="h-5 w-5 flex-shrink-0 text-[#FF4D4D]" />
            )}
            <p className="text-sm font-semibold text-[var(--c-text)]">
              {result.valid
                ? `"${result.name}" meets basic Texas LLC naming requirements!`
                : `"${result.name}" has issues that need to be fixed`}
            </p>
          </div>

          {/* Errors */}
          {result.errors.length > 0 && (
            <div className="space-y-1.5">
              {result.errors.map((err) => (
                <div key={`err-${err}`} className="flex items-start gap-2 text-sm text-[#FF4D4D]">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>{err}</span>
                </div>
              ))}
            </div>
          )}

          {/* Warnings */}
          {result.warnings.length > 0 && (
            <div className="space-y-1.5">
              {result.warnings.map((warn) => (
                <div key={`warn-${warn}`} className="flex items-start gap-2 text-sm text-amber-600">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>{warn}</span>
                </div>
              ))}
            </div>
          )}

          {/* Search links */}
          {result.valid && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">
                Now verify it&apos;s available:
              </p>
              <a
                data-testid="sos-search-link"
                href={sosSearchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2.5 text-sm font-medium text-[var(--c-text)] transition-all hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
              >
                <ExternalLink className="h-4 w-4 text-[#1E3A8A]" />
                Search Texas SOS Database (SOSDirect)
              </a>
              <a
                data-testid="comptroller-search-link"
                href={comptrollerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2.5 text-sm font-medium text-[var(--c-text)] transition-all hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
              >
                <ExternalLink className="h-4 w-4 text-[#1E3A8A]" />
                Search Business Names (Texas Comptroller)
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
